<script lang="ts">
/**
 * The lightning histogram: strikes per minute over the last half hour, for
 * whatever the map is currently showing.
 *
 * In its own glass strip, the same one the radar forecast uses, so the two
 * layers read as the same app. In a row with the colour scale and the repo
 * links it would be about 45px tall, its axis labels overlapping the legend
 * beside it.
 *
 * The difference from the radar strip: this covers the viewport's bounding
 * box, not a point. So the title names the area at whatever scale the zoom is
 * showing (a city close in, a state or a country further out), and there is
 * no "My Location" action, because a viewport is not
 * somewhere you can return from.
 */
import { onDestroy } from "svelte";
import { get } from "svelte/store";
import { _, locale } from "svelte-i18n";
import type { Translate } from "../locale/t";
import { Chart } from "chart.js";
import { toLonLat, transformExtent } from "ol/proj";
import { unByKey } from "ol/Observable";
import type { Map } from "ol";
import type { EventsKey } from "ol/events";
import { fromExtent } from "ol/geom/Polygon";
import { fetchLightningStats } from "../api";
import { LightningColors } from "../colormaps";
import { yTickLabel } from "../lib/lightningChart";
import { mapTapped, sharedActiveCap } from "../stores";
import { reverseGeocode, scaleForZoom } from "../lib/reverseGeocode";
import { onWake } from "../lib/wakeup";
import { reportShown } from "../lib/sentry";
import DismissableStrip from "./DismissableStrip.svelte";
import ChartSkeleton from "./ChartSkeleton.svelte";

export let layerManager;

/** How many minutes the histogram covers, one bar each. */
const BINS = 30;

let chart;
let loading = true;
let unavailable = false;
let noLightning = false;
/** No 2D context to draw the counts on; see `canvasInit`. */
let cannotDraw = false;
let delayedLoader: ReturnType<typeof setTimeout> | null = null;

let dismissed = false;
$: if ($sharedActiveCap !== "lightning") dismissed = false;

const subscriptions = [
  // Tapping the map is asking about what is on it; bring a cleared strip back.
  mapTapped.subscribe((n) => { if (n > 0) dismissed = false; }),
];
onDestroy(() => {
  subscriptions.forEach((unsubscribe) => unsubscribe());
  if (delayedLoader) clearTimeout(delayedLoader);
  detachMap();
});

/**
 * The current map's bounding box as a ring, in lat/lon.
 *
 * Null unless the map has been laid out. A map exists before it has a size
 * (it is created up front and only gets a target when its layer comes on
 * screen), and calculateExtent() with no size returns a zero-area extent
 * around the centre, which fromExtent() turns into a degenerate ring: a point,
 * sent to the backend as the area to count strikes in. The answer comes back
 * empty, and the strip would draw it as "no lightning in this area" when
 * nobody has asked about an area yet.
 *
 * update() already bails on null, so refusing here stops the request at source.
 */
function viewportRing() {
  const map = layerManager?.getCurrentMap?.();
  if (!map) return null;
  const size = map.getSize();
  if (!size || size[0] <= 0 || size[1] <= 0) return null;
  const extent = transformExtent(
    map.getView().calculateExtent(size),
    "EPSG:3857",
    "EPSG:4326",
  );
  return fromExtent(extent).getLinearRing(0)?.getCoordinates() ?? null;
}

/* The name of what is on screen. Geocoded from the centre of the view, at the
   granularity the zoom justifies: naming a whole country after the village
   under the middle pixel would be worse than not naming it at all. */
let placeName: string | null = null;
let placeToken = 0;

async function resolvePlace() {
  const map = layerManager?.getCurrentMap?.();
  if (!map) return;
  const view = map.getView();
  const centre = view.getCenter();
  if (!centre) return;
  const [lon, lat] = toLonLat(centre);
  const token = ++placeToken;
  const name = await reverseGeocode(
    lat,
    lon,
    get(locale) ?? "en",
    scaleForZoom(view.getZoom() ?? 0),
    "lightning",
  );
  if (token === placeToken) placeName = name;
}

$: title = placeName
  ? $_("lightning_in", { values: { place: placeName } })
  : $_("lightning_in_view");

function redraw(data: number[]) {
  if (!chart?.options.scales?.y) return;
  chart.data.datasets[0].data = data;
  chart.options.scales.y.max = Math.max(...data);
  chart.update();
}

/** How long to wait for a map that exists but has not been laid out yet. */
const LAYOUT_RETRY_MS = 250;

/**
 * Which request the strip is waiting for.
 *
 * A pan that settles while the last box's counts are still on their way asks
 * for the new box alongside, and on a slow link the old box's answer can land
 * last and be drawn under the new one's name. So each request takes a number, and only the newest is drawn. A
 * movestart takes one as well, because whatever was out then was asked about
 * a view that is going away.
 */
let statsToken = 0;

async function update() {
  if (cannotDraw) {
    // Outdating any count already asked for, which would land over a canvas
    // it cannot be drawn on and take the message back down.
    statsToken += 1;
    loading = false;
    noLightning = true;
    unavailable = true;
    return;
  }
  const polygon = viewportRing();
  if (!polygon) {
    /* Not an error and not an empty result: the map is on its way onto the
       page. Come back for it, because the moveend that would otherwise be the
       next prompt may never fire: nothing has to move for a map to finish
       being laid out. The skeleton stays up in the meantime. */
    if (delayedLoader) clearTimeout(delayedLoader);
    delayedLoader = setTimeout(() => { delayedLoader = null; update(); }, LAYOUT_RETRY_MS);
    return;
  }
  resolvePlace();
  const token = ++statsToken;
  try {
    const data = await fetchLightningStats(polygon);
    if (token !== statsToken) return;
    unavailable = false;
    noLightning = data.bins.reduce((total, bin) => total + bin, 0) === 0;
    if (!noLightning) redraw(data.bins);
  } catch (error) {
    if (token !== statsToken) return;
    noLightning = true;
    unavailable = true;
    reportShown("lightning-stats", error);
  } finally {
    if (token === statsToken) loading = false;
  }
}

/** "-30 min" … "now", one per bin; the last is a word, so it takes `$_`. */
function axisLabels(t: Translate): string[] {
  return Array(BINS).fill(null)
    .map((_unused, i) => (i === BINS - 1 ? t("now") : `-${BINS - i} min`));
}

// Re-labelled when the language changes, since the chart outlives it.
$: if (chart) {
  chart.data.labels = axisLabels($_);
  chart.update();
}

function canvasInit(elem: HTMLCanvasElement) {
  /* iOS stops handing out 2D contexts once the page's canvases have used up
     its canvas memory. Chart.js would then report "can't acquire context" as
     an error and leave a chart that cannot draw; the strip says the counts
     are unavailable instead. */
  const context = elem.getContext("2d");
  if (!context) {
    cannotDraw = true;
    reportShown("lightning-stats", new Error("No 2D canvas context for the lightning chart"));
    update();
    return undefined;
  }
  chart = new Chart(context, {
    type: "bar",
    data: {
      labels: axisLabels($_),
      datasets: [
        {
          data: Array(BINS).fill(0),
          backgroundColor: Array(BINS).fill(null).map((_unused, i) => LightningColors[
            Math.min((BINS - i) - Math.max(-20 * (BINS - i), -30), LightningColors.length - 1)
          ]),
        },
      ],
    },
    options: {
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      layout: { padding: { left: 0, right: 0, top: 4, bottom: 0 } },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false, tickLength: 1 },
          // Chart.js 4 split the axis rule out of `grid`; left on, it draws a
          // fixed hairline across the glass that does not move with the map.
          border: { display: false },
          ticks: { padding: 0, maxRotation: 0, minRotation: 0, autoSkipPadding: 24 },
        },
        y: {
          type: "linear",
          grid: { display: false },
          border: { display: false },
          min: 0,
          max: 300,
          ticks: {
            callback(value) {
              return yTickLabel(Number(value), Number(this.chart.options.scales?.y?.max ?? 0));
            },
          },
        },
      },
    },
  });
  update();
  return {
    destroy() {
      chart?.destroy();
      chart = null;
    },
  };
}

/**
 * Reload when the view settles, not while it is moving: every intermediate
 * frame of a pan would be its own query over a box nobody is looking at yet.
 *
 * Attached when the lightning layer becomes the one on screen rather than on
 * mount: this component exists for the whole session, and at mount there is no
 * current capability for LayerManager to hand back a map for. Detached again on
 * the way out, because OpenLayers listeners outlive whatever added them.
 */
let attachedMap: Map | null = null;
let moveKeys: EventsKey[] = [];

function detachMap() {
  /* Before the early return: the layout retry reschedules itself, so a pending
     one outlives the layer it was waiting for and would eventually query
     whatever map the app has switched to. */
  if (delayedLoader) {
    clearTimeout(delayedLoader);
    delayedLoader = null;
  }
  const map = attachedMap;
  if (!map) return;
  moveKeys.forEach((key) => unByKey(key));
  moveKeys = [];
  attachedMap = null;
}

function attachMap(map: Map) {
  detachMap();
  attachedMap = map;
  moveKeys = [
    map.on("movestart", () => {
      if (delayedLoader) {
        clearTimeout(delayedLoader);
        delayedLoader = null;
      }
      statsToken += 1;
      loading = true;
    }),
    map.on("moveend", () => {
      /* Forgotten as soon as it fires. Held until the request it started came
         back, the handle would mean two things: a moveend in that window would
         be dropped as though a load were already pending, and the request's
         `finally` would clear whatever timer was pending by then, which the
         next movestart could no longer cancel. */
      if (delayedLoader) clearTimeout(delayedLoader);
      delayedLoader = setTimeout(() => { delayedLoader = null; update(); }, 650);
    }),
  ];
  update();
}

/*
 * Asked again on a wake. Otherwise a failed count would stay "unavailable"
 * until the map was moved, and a good one would show whatever the half hour
 * had been when it was asked: the axis says "the last 30 minutes", and after a phone has been in a
 * pocket those are other minutes. A failed strip goes back to the skeleton
 * while it asks; a good one stays up until the newer answer replaces it.
 * Only while attached: without the layer up there is no view to ask about.
 */
subscriptions.push(onWake(() => {
  if (!attachedMap) return;
  if (unavailable) loading = true;
  update();
}));

$: {
  if ($sharedActiveCap === "lightning") {
    const map = layerManager?.getCurrentMap?.();
    if (map && map !== attachedMap) attachMap(map);
  } else {
    detachMap();
  }
}

</script>

<style>
  /* The plot spans the strip, inset only by the tray padding: unlike the radar
     strip there is no scrubber underneath for the bars to line up with. */
  .plot {
    position: relative;
    height: 100%;
    margin: 0 var(--mc-tray-pad);
  }
  .canvas-wrap {
    height: 100%;
  }
  .canvas-wrap canvas {
    display: block;
  }

  .dim {
    opacity: 0.4;
  }
  /* The canvas keeps its box while the skeleton is up: Chart.js sizes itself
     from the element, and a display:none parent would measure it at zero and
     draw the first frame into a 0x0 canvas. */
  .canvas-wrap.hidden {
    visibility: hidden;
  }

  /* Sits over the plot rather than replacing it, so the strip keeps its height
     and nothing below it moves when the answer arrives. */
  .empty {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
    color: var(--mc-text);
    font: 600 12px/1.3 var(--mc-font);
  }
</style>

{#if $sharedActiveCap === "lightning" && !dismissed}
  <DismissableStrip {title} collapsed on:dismiss={() => { dismissed = true; }}>
    <div class="plot">
      {#if loading}
        <ChartSkeleton bars={BINS} />
      {:else if noLightning}
        <div class="empty">
          {unavailable ? $_("lightning_unavailable") : $_("lightning_none")}
        </div>
      {/if}
      <div class="canvas-wrap" class:hidden={loading} class:dim={noLightning || unavailable}>
        <canvas use:canvasInit></canvas>
      </div>
    </div>
  </DismissableStrip>
{/if}
